/* Copyright (c) 2020-present CloudIO Inc */

import {
  IoDatasource,
  IoDatasourceAttributes,
  IoFunctions,
  IoPages,
  Query,
  Row,
} from '@cloudio-saas/datasource-types';
import {
  useStateXAtom,
  useStateXAtomSetter,
  useStateXAtomValue,
  useStateXGetter,
  useStateXResetter,
  useStateXSetter,
} from '@cloudio-saas/statex';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  Theme,
  Typography,
} from '@mui/material';
import Report, { EvaluationResult } from './Report';
import { makeStyles } from '@mui/styles';
import { useStoreAttribute } from 'components/DataStoreReference/DataStoreReference';
import CodeInput from 'custom/CodeInput';
import useComponentSize from 'hooks/useComponentSize';
import Icon from 'icons/Icon';
import {
  useBackendCodeEditorTypes,
  useCodeEditorTypes,
} from 'layout/Sidebar/panels/DataSourceView/CodeEditorTypes';
import { selectionAtom } from 'layout/Sidebar/panels/DataSourceView/DataSourceScriptHistory';
import { camelCase, upperFirst } from 'lodash-es';
import { usePending, usePlatform } from 'platform/PlatformHooks';
import React, {
  memo,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import Store from 'store/Store';
import { isFromDB } from 'store/storeTypes';
import useAppStore, {
  useStoreCurrentRecord,
  useStoreCurrentRecordId,
  useRecord,
} from 'store/useAppStore';
import {
  getIoDatasourceAttributesStoreProps,
  getIoDatasourceStoreProps,
  useIoDatasourceEditStore,
} from 'store/utils/utils';
import { useTheme } from 'theme/Theme';
import { stopPropagation } from 'utils/events';
import WaveDots from 'utils/WaveDots';
import { isNotEmpty, shrink } from '../../../../utils/isEmpty';
import {
  activeSidebarPanelAtom,
  DSView,
  propertySectionExpandedAtom,
  sidebarActiveDatasourceHistoryAtom,
  sidebarActiveDatasourceIdAtom,
  sidebarDatasourceEditModeAtom,
  sidebarFunctionsFilterAtom,
  sidebarPagesFilterAtom,
} from '../../atoms';
import Checkbox from '../fields/Checkbox';
import TextField from '../fields/TextField';
import { scripts, selectedTabAtom } from './constants';
import ExternalDataSourceSelect from './ExternalDataSourceSelect';
import { downloadPLSQL, downloadPLSQLCRUD } from './PLSQLGeneration';
import useUser, { isViewOnlyDeveloper } from 'root/useUser';
import useDataStore from 'store/useDataStore';
import stringify from 'json-stable-stringify';
import { getURLQueryStringToPost } from 'utils/url';
import { getUrl } from 'config/config';
import { useAppID } from 'designer/atoms';
import CustomTemplates from './CustomTemplates';
import ConnectionSelect from '../ApplicationsView/ConnectionSelect';
import SelectInput from 'custom/SelectInput';
import { timeZoneOptions } from 'utils/dateUtils';
import { Platform } from 'componentregistry';

const regex = /^[a-zA-Z0-9]*$/;

const useStyles = makeStyles(() => ({
  root: {
    padding: '0 20px',
    display: 'flex',
    flexDirection: 'column',
    position: 'absolute',
    bottom: 0,
    top: 0,
    left: 0,
    right: 0,
    overflow: 'hidden',
  },
  editor: {
    flex: 1,
  },
  form: {
    flexBasis: 'auto',
    flex: 0,
    '& > *': {
      marginTop: 8,
    },
  },
}));

export const useAccordianStyles = makeStyles((theme: Theme) => ({
  root: {
    boxShadow: 'none',
    padding: 8,
    margin: 0,
    '&.Mui-expanded': {
      margin: 0,
    },
  },
  header: {
    padding: 0,
    minHeight: 40,
    '& .MuiAccordionSummary-content': {
      margin: 0,
    },
    '&.Mui-expanded': {
      minHeight: 40,
    },
    '& > div': {
      display: 'flex',
      alignItems: 'center',
    },
  },
  details: {
    padding: 0,
    '&:last-child': {},
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: theme.typography.fontWeightRegular,
    flex: 1,
  },
  defaultIcon: {
    marginRight: 20,
  },
  form: {
    display: 'block',
    width: '100%',
  },
}));

const useCBStyles = makeStyles((theme: Theme) => ({
  closeButton: {
    position: 'absolute',
    right: theme.spacing(2),
    top: theme.spacing(2),
  },
  body: {
    padding: 0,
    margin: 0,
    overflow: 'scroll',
  },
}));

const SampleDataPopUp = ({
  platform,
  attributeCode,
  store,
  openPopup,
  title,
  appUid,
  close,
  value,
  onChange,
}: {
  platform: Platform;
  attributeCode: keyof IoDatasource;
  store: Store<IoDatasource>;
  openPopup: boolean;
  title: string;
  appUid: string;
  close: () => void;
  value: string | undefined;
  onChange: ((valueOnChange: string) => void) | undefined;
}) => {
  const s = useComponentSize();
  const {
    store: attributestore,
    records,
    recordsOrder,
  } = useAppStore<IoDatasourceAttributes>(
    getIoDatasourceAttributesStoreProps(appUid),
  );
  const get = useStateXGetter();
  const datasourceUid = get(sidebarActiveDatasourceIdAtom);
  useEffect(() => {
    if (openPopup) {
      attributestore.query({
        props: {
          filter: [
            { appUid: { is: appUid } },
            { datasourceUid: { is: datasourceUid } },
          ],
          limit: 2000,
        },
      });
    }
  }, [openPopup, attributestore, appUid, datasourceUid]);

  const names = recordsOrder
    .filter(
      (item) =>
        !['createdBy', 'creationDate', 'lastUpdateDate', 'lastUpdatedBy'].some(
          (key) => item.includes(key),
        ),
    )
    .map((item, index) => {
      return index === 0
        ? `"${records[item].attributeName}": "${records[item].attributeType} Value"`
        : `\n    "${records[item].attributeName}": "${records[item].attributeType} Value"`;
    });
  const theme = useTheme();
  const user = useUser();
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [validationResponse, setValidationResponse] =
    useState<EvaluationResult | null>(null);
  const [result, setResult] = useState<{
    type: 'success' | 'error';
    msg: string;
  } | null>(null);
  const recordId = useStoreCurrentRecordId(store);
  const record = useStoreCurrentRecord(store);
  useEffect(() => {
    setOpen(openPopup);
    setResult(null);
  }, [openPopup]);

  const jsonTemplate = useMemo(() => {
    if (attributeCode === 'preQueryScript') {
      return `{
  "fullSQL": "Select * from ${datasourceUid}",
  "aggregateList": [],
  "data": null,
  "fetchDistinct": null,
  "filter": [
    {
      "task": {
        "like": "123"
      }
    }
  ],
  "groupByAttributeCodeList": [],
  "limit": 20,
  "offset": 0,
  "orderByClause": "",
  "params": null,
  "projection": null,
  "selectAttributeCodeList": null,
  "sort": null,
  "whereClause": "",
  "fromClause": "",
  "whereClauseParamList": [],
  "pagination": null,
  "includeRowCount": null
}`;
    }
    if (names.length > 0) {
      return `[
  {
    ${names}
  }
]`;
    }
    return '';
  }, [attributeCode, datasourceUid, names]);

  useEffect(() => {
    setState(jsonTemplate);
    setResult(null);
  }, [attributeCode, jsonTemplate]);
  useEffect(() => {
    setOpen(openPopup);
  }, [openPopup]);

  const [, setState] = useState(() => {
    return '';
  });

  const cbClasses = useCBStyles();

  const handleClose = () => {
    setOpen(false);
    setResult(null);
    close();
  };

  const handleOk = async () => {
    if (recordId) {
      setIsLoading(true);
      setResult(null);
      // store.updateRecord(recordId, { sampleScriptData: state });
      try {
        const jsCode = record?.record?.[attributeCode] as string;
        if (!jsCode) return;

        const response = await fetch(
          `${getUrl()}validate_js_script?${getURLQueryStringToPost(user)}`,
          {
            method: 'POST',
            credentials: 'include',
            headers: {
              'X-Application': appUid,
              'Content-Type': 'text/plain',
            },
            body: jsCode,
          },
        );
        if (!response.ok) {
          const text = await response.text();
          platform.showError(
            `Validate Script failed. Server response: ${
              text || 'Unknown error'
            }`,
          );
          return false;
        }
        const validationResult: EvaluationResult = await response.json();
        setValidationResponse(validationResult);
      } catch (e) {
        console.error('validate_js_script failed', e);
        platform.showError('Unexpected error while validating script');
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      // maxWidth={false}
      // fullWidth={false}
      PaperProps={{
        sx: {
          '&.MuiDialog-paper': {
            maxWidth: 'none !important',
          },
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: isLoading ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.3)',
          },
        },
      }}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
      onMouseDown={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
    >
      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            bgcolor: 'rgba(0,0,0,0.3)',
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress color="primary" />
        </Box>
      )}
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <Typography sx={{ flex: 1 }}>
            Validate {title} Script: Input
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          className={cbClasses.closeButton}
          onClick={handleClose}
          sx={{ ml: result ? 2 : 0 }}
        >
          <span aria-hidden="false" style={{ display: 'none' }}>
            Close
          </span>
          <Icon iconName="times" />
        </IconButton>
      </DialogTitle>
      <DialogContent
        className={cbClasses.body}
        sx={{ position: 'relative', minWidth: 1016, height: 1200 }}
      >
        <Paper
          variant="outlined"
          sx={{
            display: 'flex',
            height: '100%',
            minHeight: 500,
            overflow: 'hidden',
            borderColor: 'divider',
          }}
        >
          <div ref={s.ref} style={{ height: '100%', flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                borderRight: validationResponse ? 1 : 0,
                borderColor: 'divider',
                height: '100%',
              }}
            >
              <Box
                sx={{
                  p: 1.5,
                  borderBottom: 1,
                  borderColor: 'divider',
                  bgcolor: 'background.default',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  color="text.secondary"
                >
                  Configuration Script
                </Typography>
              </Box>
              <Box sx={{ position: 'relative', flex: 1, overflow: 'hidden' }}>
                <div>
                  <CodeInput
                    // disabled={isViewOnlyUser}
                    darkMode={theme.palette.mode !== 'light'}
                    height={s.height}
                    width={s.width}
                    value={value ?? ''}
                    onChange={onChange}
                    language="typescript"
                  />
                </div>
              </Box>
            </Box>
          </div>
          {validationResponse ? (
            <Box
              sx={{
                flex: 2,
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'background.paper',
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  p: 1.5,
                  borderBottom: 1,
                  borderColor: 'divider',
                  bgcolor: 'background.default',
                }}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  color="text.secondary"
                >
                  Validation Report
                </Typography>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  p: 2,
                  overflow: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Report result={validationResponse} />
              </Box>
            </Box>
          ) : (
            <></>
          )}
        </Paper>
      </DialogContent>
      <DialogActions>
        <div
          style={{
            display: 'flex',
            flexDirection: result?.type === 'success' ? 'row' : 'column',
            alignItems: 'center',
            flex: 1,
            justifyContent: 'space-between',
          }}
        >
          {result &&
            (result?.type === 'success' ? (
              <Chip
                icon={
                  <Icon
                    iconName={
                      result.type === 'success' ? 'check-circle' : 'bug'
                    }
                    iconType="solid"
                    color={'white'}
                  />
                }
                label={`${result.msg}`}
                color={result.type === 'success' ? 'success' : 'error'}
                variant="filled"
              />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0px 8px',
                  borderRadius: '4px',
                  marginBottom: '8px',
                  minWidth: '500px',
                }}
              >
                <Icon
                  iconName={'bug'}
                  iconType="solid"
                  color={'red'}
                  sx={{ flex: 1 }}
                />
                <Typography
                  sx={{
                    color: 'red',
                    flex: 14,
                    maxWidth: '500px',
                    overflow: 'auto',
                  }}
                >
                  {result?.msg}
                </Typography>
              </Box>
            ))}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleOk}
              startIcon={
                isLoading ? (
                  <WaveDots active />
                ) : (
                  <Icon iconName="circle-check" color="#fff" />
                )
              }
              disabled={isLoading}
            >
              {validationResponse ? 'Re-Validate' : 'Validate'}
            </Button>
            <Button
              variant="contained"
              onClick={handleClose}
              startIcon={<Icon iconName="times-circle" color="#fff" />}
            >
              Close
            </Button>
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export function EditDsName({
  datasourceUid,
  onClose,
}: {
  datasourceUid: string;
  onClose: () => void;
}) {
  const appUid = useAppID();
  const user = useUser();
  const platform = usePlatform();
  const [newDsName, setNewDsName] = useState<string>('');
  const set = useStateXSetter();
  const store = useDataStore<IoDatasource>(getIoDatasourceStoreProps(appUid));
  const [pending, setPending] = usePending();

  const onSave = async () => {
    platform.startAsyncTransition(async () => {
      const body = stringify({
        data: {
          action: 'UPDATE_DATASOURCE_NAME',
          oldDsName: datasourceUid,
          newDsName,
        },
      });

      const response = await fetch(
        `${getUrl()}util?${getURLQueryStringToPost(user)}`,
        {
          credentials: 'include',
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-application': appUid,
          },
          body,
        },
      );
      const json = await response.json();
      if (json.status === 'OK') {
        onClose();
        store.refresh();
        set(sidebarActiveDatasourceIdAtom, newDsName);
        platform.showSuccess('Datasource Name updated successfully!');
      } else {
        const message = isNotEmpty(json.message)
          ? json.message
          : JSON.parse(json?.bodyText ?? '')?.error;
        platform.showError(`${message}`);
        console.error('Error while updating datasource name', json);
      }
    }, setPending);
  };
  const content = (
    <Box
      sx={{
        display: 'flex',
        minWidth: '500px',
        p: '24px',
        gap: '12px',
        flexDirection: 'column',
      }}
    >
      <TextField
        value={datasourceUid}
        disabled={true}
        data-testid="edit-ds-name"
        label="Old Datasource Name"
      />
      <TextField
        data-testid="edit-ds-name"
        label="New Datasource Name"
        value={newDsName}
        onChange={(e) => {
          const value = e.target.value.endsWith(' ')
            ? e.target.value
            : shrink(upperFirst(camelCase(e.target.value)));
          setNewDsName(value);
        }}
        error={newDsName === datasourceUid || !regex.test(newDsName)}
        helperText={
          newDsName === datasourceUid
            ? "New name can't be same as old name!"
            : !regex.test(newDsName)
            ? 'Datasource name cannot contain spaces or special characters. Please remove any spaces or special characters from the name!'
            : undefined
        }
        inputProps={{
          maxLength: 40,
        }}
        sx={{
          '& .MuiFormHelperText-root': {
            width: 450,
          },
        }}
      />
    </Box>
  );
  return (
    <Dialog
      onClick={stopPropagation}
      onClose={onClose}
      onMouseDown={stopPropagation}
      open
    >
      <DialogTitle sx={{ borderBottom: '2px solid #157CEC' }}>
        <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight={700}>
            {`Edit ${datasourceUid} Datasource Name`}
          </Typography>
        </Box>
        <IconButton
          data-testid="close"
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
          }}
          onClick={onClose}
          disabled={pending}
        >
          <span aria-hidden="false" style={{ display: 'none' }}>
            Close
          </span>
          <Icon iconName="times" />
        </IconButton>
      </DialogTitle>
      <List sx={{ pt: 0 }}>{content}</List>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '8px',
          p: '4px 20px 16px 20px',
        }}
      >
        <Button
          variant="contained"
          color="secondary"
          startIcon={<Icon iconName="times" color="#fff" />}
          onClick={onClose}
          data-testid="edit-ds-cancel-btn"
          disabled={pending}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          data-testid="edit-ds-save-btn"
          startIcon={
            pending ? (
              <WaveDots active />
            ) : (
              <Icon iconName="save" color="#fff" />
            )
          }
          onClick={onSave}
          disabled={
            !newDsName ||
            newDsName === datasourceUid ||
            pending ||
            !regex.test(newDsName)
          }
        >
          Save
        </Button>
      </Box>
    </Dialog>
  );
}

export function UsageInfoPopup({
  appUid,
  datasourceUid,
  onClose,
  openEditDsNamePopup,
  setView,
}: {
  appUid: string;
  datasourceUid: string;
  onClose: () => void;
  openEditDsNamePopup: () => void;
  setView: (value: DSView) => void;
}) {
  const usageInPage = useDataStore<IoPages>({
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoPages',
    alias: 'IoPagesDSRefAlias',
  });
  const usageInFunction = useDataStore<IoFunctions>({
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoFunctions',
    alias: 'IoFunctionsDSRefAlias',
  });
  const usageInDs = useDataStore<IoDatasource>({
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoDatasource',
    alias: 'IoDSRefAlias',
  });
  const pageCount = usageInPage.recordsOrder().length;
  const functionCount = usageInFunction.recordsOrder().length;
  const dsCount = usageInDs.recordsOrder().length;
  const set = useStateXSetter();

  const content = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        padding: '0px 24px',
        minWidth: '600px',
      }}
    >
      {pageCount ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            padding: '12px',
          }}
        >
          <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <Icon iconName="desktop" iconType="solid" />
            <Typography variant="h6" fontWeight={700}>
              Pages
            </Typography>
            <Chip label={pageCount} size="small" />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              pt: '8px',
              pl: '24px',
              gap: '4px',
              maxHeight: '200px',
              overflowY: 'auto',
            }}
          >
            {Object.keys(usageInPage.records()).map((r, index) => {
              const pageRec = usageInPage.records()[r];
              return (
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}
                >
                  <Typography variant="body1">{`${index + 1}. ${
                    pageRec?.pageName
                  } (${pageRec?.pageUid})`}</Typography>
                  <Icon
                    iconName="external-link-alt"
                    onClick={() => {
                      onClose();
                      set(activeSidebarPanelAtom, 'pages');
                      set(sidebarPagesFilterAtom, `pg:${pageRec?.pageName}`);
                    }}
                    iconType="regular"
                    color="primary"
                    height={12}
                    width={12}
                    data-tip={`Go to page ${pageRec?.pageName}`}
                  />
                </Box>
              );
            })}
          </Box>
        </Box>
      ) : null}
      {functionCount ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', padding: '12px' }}>
          <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <Icon iconName="code" iconType="solid" />
            <Typography variant="h6" fontWeight={700}>
              Functions
            </Typography>
            <Chip label={functionCount} size="small" />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              pt: '4px',
              pl: '24px',
              gap: '4px',
              maxHeight: '200px',
              overflowY: 'auto',
            }}
          >
            {Object.keys(usageInFunction.records()).map((r, index) => {
              const fnRec = usageInFunction.records()[r];
              return (
                <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <Typography variant="body1">{`${index + 1}. ${
                    fnRec?.functionName
                  } (${fnRec?.functionUri})`}</Typography>
                  <Icon
                    iconName="external-link-alt"
                    onClick={() => {
                      onClose();
                      set(activeSidebarPanelAtom, 'functions');
                      set(
                        sidebarFunctionsFilterAtom,
                        fnRec?.functionName ?? '',
                      );
                    }}
                    iconType="regular"
                    color="primary"
                    height={12}
                    width={12}
                    data-tip={`Go to function ${fnRec?.functionName}`}
                  />
                </Box>
              );
            })}
          </Box>
        </Box>
      ) : null}
      {dsCount ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', padding: '12px' }}>
          <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <Icon iconName="coins" iconType="solid" />
            <Typography variant="h6" fontWeight={700}>
              Datasource Scripts
            </Typography>
            <Chip label={dsCount} size="small" />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              pt: '4px',
              pl: '24px',
              gap: '4px',
              maxHeight: '200px',
              overflowY: 'auto',
            }}
          >
            {Object.keys(usageInDs.records()).map((r, index) => {
              const dsRec = usageInDs.records()[r];
              return (
                <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <Typography variant="body1">
                    {`${index + 1}. ${dsRec?.datasourceUid}`}
                  </Typography>
                  <Icon
                    iconName="external-link-alt"
                    onClick={() => {
                      set(
                        sidebarActiveDatasourceIdAtom,
                        dsRec?.datasourceUid ?? '',
                      );
                      onClose();
                    }}
                    iconType="regular"
                    color="primary"
                    height={12}
                    width={12}
                    data-tip={`Go to ${dsRec?.datasourceUid} datasource scripts`}
                  />
                </Box>
              );
            })}
          </Box>
        </Box>
      ) : null}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '8px',
          p: '8px 0px',
        }}
      >
        <Button
          variant="contained"
          color="secondary"
          startIcon={<Icon iconName="times" color="#fff" />}
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<Icon iconName="edit" color="#fff" />}
          onClick={() => {
            onClose();
            openEditDsNamePopup();
          }}
        >
          Edit
        </Button>
      </Box>
    </Box>
  );

  return (
    <Dialog
      onClick={stopPropagation}
      onClose={onClose}
      onMouseDown={stopPropagation}
      open
    >
      <DialogTitle sx={{ borderBottom: '2px solid #157CEC' }}>
        <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight={700}>
            {datasourceUid}
          </Typography>
          <Typography variant="body1">used In below places</Typography>
        </Box>
        <IconButton
          data-testid="close"
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
          }}
          onClick={onClose}
        >
          <span aria-hidden="false" style={{ display: 'none' }}>
            Close
          </span>
          <Icon iconName="times" />
        </IconButton>
      </DialogTitle>
      <List sx={{ pt: 0 }}>{content}</List>
    </Dialog>
  );
}

const AllScriptAccordians = memo(
  ({
    store,
    recordId,
    aggr,
    setView,
    appUid,
    isViewOnlyUser = false,
  }: {
    store: Store<IoDatasource>;
    recordId: string;
    aggr: boolean;
    setView: (_view: DSView) => void;
    appUid: string;
    isViewOnlyUser: boolean;
  }): React.ReactElement | null => {
    const theme = useTheme();
    const classes = useStyles();
    const { record } = useRecord(store, recordId);
    const reset = useStateXResetter();
    const platform = usePlatform();
    const [isPending, setPending] = usePending();
    const [open, setOpen] = useState(false);

    //     const validateStore = useDataStore<IoDatasource>({
    //   appUid,
    //   pageId: '$$Sidebar$$',
    //   ds: 'IoDsValidateScript',
    //   alias: `dsValidateScript_${appUid}`,
    // });

    let tabs = scripts.filter(
      (s) =>
        s.attribute !== 'pageController' &&
        s.attribute !== 'WebAPI' &&
        s.attribute !== 'Module' &&
        s.attribute !== 'ScheduledJob' &&
        s.attribute !== 'wf' &&
        s.attribute !== 'authApi' &&
        s.attribute !== 'describeApi' &&
        s.attribute !== 'postApi' &&
        s.attribute !== 'queryApi' &&
        s.attribute !== 'componentJs' &&
        s.attribute !== 'TestScripts' &&
        s.attribute !== 'managerJs' &&
        s.attribute !== 'wfNode',
    );
    if (aggr) {
      tabs = tabs.filter((tab) => !tab.attribute.startsWith('after'));
    }
    tabs.push({
      attribute: 'customTemplates',
      title: 'Custom Templates',
      defaultScript: '',
      queryVars: '',
    });

    const [activeTab, setActiveTab] = useStateXAtom(selectedTabAtom);

    const ds = record?.datasourceUid;
    useBackendCodeEditorTypes();
    useCodeEditorTypes(ds, activeTab.attribute);
    const fromDB = isFromDB(record);

    const [value, setValue] = useStoreAttribute<
      string | undefined,
      IoDatasource
    >(store, recordId, activeTab.attribute as string, '');

    const setAuditId = useStateXAtomSetter(sidebarActiveDatasourceHistoryAtom);
    const s = useComponentSize();

    if (!record) {
      return null;
    }

    // console.log('s...', s);

    return (
      <>
        <Tabs
          value={activeTab.index}
          onChange={(e, val) =>
            setActiveTab({ index: val, attribute: tabs[val].attribute })
          }
          variant="scrollable"
          scrollButtons="auto"
        >
          {tabs.map((script) => {
            const attr = script.attribute;
            if (
              attr === 'Module' ||
              attr === 'ScheduledJob' ||
              attr === 'WebAPI' ||
              attr === 'authApi' ||
              attr === 'describeApi' ||
              attr === 'pageController' ||
              attr === 'postApi' ||
              attr === 'queryApi' ||
              attr === 'wf' ||
              attr === 'componentJs' ||
              attr === 'managerJs' ||
              attr === 'TestScripts' ||
              attr === 'wfNode'
            ) {
              return null;
            }
            if (attr === 'customTemplates') {
              return (
                <Tab
                  data-testid={`${script.attribute}-tab`}
                  key={script.attribute}
                  label={script.title}
                  icon={
                    <Icon
                      iconName="file-excel"
                      color={
                        activeTab.attribute === script.attribute
                          ? 'info'
                          : undefined
                      }
                    />
                  }
                  iconPosition="start"
                />
              );
            }
            return (
              <Tab
                data-testid={`${script.attribute}-tab`}
                key={script.attribute}
                label={
                  <Typography
                    component="div"
                    sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                  >
                    {script.title}
                    {record?.[attr] && fromDB ? (
                      isPending ? (
                        <WaveDots active />
                      ) : (
                        <>
                          {' '}
                          <Icon
                            onMouseDown={stopPropagation}
                            data-tip="Validate"
                            onClick={() => {
                              setOpen(true);
                            }}
                            color="secondary"
                            iconName="octagon-check"
                          />
                          <Icon
                            onMouseDown={stopPropagation}
                            data-tip="View Change History"
                            onClick={() => {
                              platform.startAsyncTransition(async () => {
                                setAuditId({
                                  attributeCode: attr,
                                  pkValue: `${record.orgUid}:${record.appUid}:${record.datasourceUid}`,
                                });
                                reset(selectionAtom);
                                setView('history');
                              }, setPending);
                            }}
                            iconName="history"
                          />
                        </>
                      )
                    ) : null}
                    {activeTab.attribute === attr ? (
                      <SampleDataPopUp
                        platform={platform}
                        attributeCode={attr}
                        value={value}
                        onChange={(val) => {
                          return setValue(val);
                        }}
                        store={store}
                        openPopup={open}
                        title={script.title}
                        appUid={appUid}
                        close={() => setOpen(false)}
                      />
                    ) : null}
                    {activeTab.attribute !== attr || value ? null : (
                      <Box
                        data-tip="Populate sample code"
                        onClick={(e) => {
                          e.stopPropagation();
                          setValue(`/*

-------------------
Available variables
-------------------

${script.queryVars}

*/

${script.defaultScript ?? ''}`);
                        }}
                      >
                        <Icon iconName="code" />
                      </Box>
                    )}
                  </Typography>
                }
                icon={
                  <Icon
                    iconName={getIconName(script.attribute)}
                    color={
                      activeTab.attribute === script.attribute
                        ? 'info'
                        : undefined
                    }
                  />
                }
                iconPosition="start"
              />
            );
          })}
        </Tabs>
        <div ref={s.ref} className={classes.editor}>
          {activeTab.attribute !== 'customTemplates' ? (
            !open && (
              <CodeInput
                disabled={isViewOnlyUser}
                darkMode={theme.palette.mode !== 'light'}
                height={s.height - 20}
                width={s.width}
                value={value ?? ''}
                onChange={setValue}
                onSave={async () => {
                  await store.save();
                }}
                language="typescript"
              />
            )
          ) : (
            <CustomTemplates
              appUid={appUid}
              platform={platform}
              width={s.width}
              height={s.height}
              ds={record.datasourceUid ?? ''}
            />
          )}
        </div>
      </>
    );
  },
);

export const editDatasourceFilter: (
  appUid: string,
  datasourceUid: string,
) => Query<IoDatasource> = (appUid, datasourceUid) => ({
  filter: [
    {
      datasourceUid: { is: datasourceUid },
    },
    { appUid: { is: appUid } },
  ],
  limit: 1,
});

function checkForLookups(record: Row<IoDatasource>) {
  return !!['lookup', 'list', 'valueset'].find(
    (key) =>
      record.datasourceUid?.toLowerCase().includes(key) ||
      record.typeValue?.toLowerCase().includes(key),
  );
}

export function getIconName(attr: string) {
  switch (attr) {
    case 'preQueryScript':
      return 'search';
    case 'postQueryScript':
      return 'database';
    case 'beforeInsertScript':
      return 'circle-plus';
    case 'beforeUpdateScript':
      return 'edit';
    case 'beforeDeleteScript':
      return 'trash-can-undo';
    case 'afterInsertScript':
      return 'file-circle-plus';
    case 'afterUpdateScript':
      return 'file-edit';
    case 'afterDeleteScript':
      return 'trash-can-xmark';
    case 'validateRowScript':
      return 'shield-check';
    case 'customTemplates':
      return 'file-excel';
    default:
      return 'code';
  }
}

const DataSourceEdit = ({
  setView,
  appUid,
  width,
}: {
  appUid: string;
  setView: (_view: DSView) => void;
  width: number;
}): React.ReactElement | null => {
  const classes = useStyles();
  const store = useIoDatasourceEditStore(appUid);
  const _id = useStateXAtomValue(sidebarActiveDatasourceIdAtom);
  const editMode = useStateXAtomValue(sidebarDatasourceEditModeAtom);
  const fromTable = editMode === 'Table';
  const fromScratch = editMode === 'Scratch';
  const fromSQL = editMode === 'SQL';
  const platform = usePlatform();
  const [showDsNameEditPopup, setDsNameEditPopup] = useState<boolean>(false);
  const [showUsageInfoPopup, setUsageInfoPopup] = useState<boolean>(false);
  const [pending, setPending] = usePending();
  const usageInPage = useDataStore<IoPages>({
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoPages',
    alias: 'IoPagesDSRefAlias',
  });
  const usageInFunction = useDataStore<IoFunctions>({
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoFunctions',
    alias: 'IoFunctionsDSRefAlias',
  });
  const usageInDs = useDataStore<IoDatasource>({
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoDatasource',
    alias: 'IoDSRefAlias',
  });

  const onEditClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();
    platform.startAsyncTransition(async () => {
      const p1 = usageInPage.query({
        props: {
          projection: {
            pageUid: 1,
            pageName: 1,
            pageIcon: 1,
          },
          filter: [
            {
              md: { like: `"dataSource": "${record?.datasourceUid}"` },
            },
          ],
        },
      });
      const p2 = usageInFunction.query({
        props: {
          projection: {
            functionUri: 1,
            functionName: 1,
          },
          filter: [
            {
              functionCode: { like: `${record?.datasourceUid}` },
            },
          ],
        },
      });
      const p3 = usageInDs.query({
        props: {
          projection: {
            datasourceUid: 1,
          },
          filter: [
            {
              anyOf: [
                { preQueryScript: { like: `${record?.datasourceUid}` } },
                { postQueryScript: { like: `${record?.datasourceUid}` } },
                { beforeInsertScript: { like: `${record?.datasourceUid}` } },
                { beforeUpdateScript: { like: `${record?.datasourceUid}` } },
                { beforeDeleteScript: { like: `${record?.datasourceUid}` } },
                { afterInsertScript: { like: `${record?.datasourceUid}` } },
                { afterUpdateScript: { like: `${record?.datasourceUid}` } },
                { afterDeleteScript: { like: `${record?.datasourceUid}` } },
                { validateRowScript: { like: `${record?.datasourceUid}` } },
              ],
            },
          ],
        },
      });
      await Promise.all([p1, p2, p3]);
      const pageCount = usageInPage.recordsOrder().length;
      const functionCount = usageInFunction.recordsOrder().length;
      const dsCount = usageInDs.recordsOrder().length;
      if (pageCount || functionCount || dsCount) {
        setUsageInfoPopup(true);
      } else {
        setDsNameEditPopup(true);
      }
    }, setPending);
  };

  if (_id) {
    // _id will be an empty string during new datasource creation
    store.read({ props: editDatasourceFilter(appUid, _id) });
  }
  const recordId = useStoreCurrentRecordId(store);
  const { record, update } = useStoreCurrentRecord(store);

  const accordianClasses = useAccordianStyles();
  const [expanded, setExpanded] = useStateXAtom(propertySectionExpandedAtom, {
    params: { section: 'ds-details' },
  });
  const isViewOnlyUser = isViewOnlyDeveloper(platform, appUid);
  const theme = useTheme();

  useLayoutEffect(() => {
    if (!(record || _id)) {
      setView('list');
    }
  }, [_id, record, setView]);

  if (!record) {
    return null;
  }

  const fromDB = isFromDB(store.getCurrentRecord());

  const tableName = (
    <TextField
      data-testid="table-name"
      autoFocus={fromTable}
      disabled={!!record.copy_from || (record.appUid === 'cloudio' && fromDB)}
      label={
        record.datasourceType === 'External'
          ? 'External DataSource Name'
          : 'Table Name'
      }
      value={record.typeValue ?? ''}
      onChange={(e) => {
        if (fromDB) {
          update({
            typeValue: e.target.value,
          });
        } else {
          update({
            typeValue: e.target.value,
            datasourceUid:
              !record.datasourceUid ||
              record.datasourceUid === upperFirst(camelCase(record.typeValue))
                ? upperFirst(camelCase(e.target.value))
                : record.datasourceUid,
          });
        }
      }}
      InputProps={{
        endAdornment: fromTable ? null : (
          <InputAdornment position="end">
            <Icon
              iconName="comment-exclamation"
              data-tip={
                "DataSource will be auto generated if the table already exists, otherwise a new table with the given name will be created with an ID & other WHO columns. Specify the table name as 'dummy' (without the quotes) for a transient DataSource to be created without any backing database table. Once the DataSource is created, you can add more attributes to it as needed. \n\n Note: If the db type is Oracle, then the table will be created in uppercase in the database. Ex: `myTable` becomes `MYTABLE`"
              }
              data-tip-delay={0}
              data-tip-width={500}
              data-tip-at="top"
            />
          </InputAdornment>
        ),
      }}
      // eslint-disable-next-line react/jsx-no-duplicate-props
      inputProps={{
        maxLength: 61,
      }}
    />
  );
  const name =
    fromDB && record.appUid !== 'cloudio' ? (
      <Box sx={{ display: 'flex', gap: '4px', flexDirection: 'column' }}>
        <Typography
          fontWeight={400}
          variant="caption"
          fontSize={10}
          sx={{ opacity: 0.7 }}
        >
          Datasource Name
        </Typography>
        <Box
          sx={{
            display: 'flex',
            gap: '8px',
            borderBottom: `1px dotted ${theme.palette.divider}`,
            pb: '4px',
          }}
        >
          <Typography sx={{ opacity: 0.5 }}>
            {record.datasourceUid ?? ''}
          </Typography>
          <Icon
            iconName="edit"
            data-tip={'Edit Datasource Name'}
            data-testid="edit-ds-name-icon"
            data-tip-delay={0}
            data-tip-width={500}
            data-tip-at="top"
            color="primary"
            onClick={onEditClick}
            disabled={pending}
          />
        </Box>
      </Box>
    ) : (
      <TextField
        data-testid="ds-name"
        autoFocus={!fromTable}
        disabled={fromDB}
        label="DataSource Name"
        value={record.datasourceUid ?? ''}
        onChange={(e) => {
          const datasourceUid = e.target.value.endsWith(' ')
            ? e.target.value
            : shrink(upperFirst(camelCase(e.target.value)));
          update({ datasourceUid });
        }}
        error={!regex.test(record.datasourceUid ?? '')}
        helperText={
          !regex.test(record.datasourceUid ?? '')
            ? 'Datasource name cannot contain spaces or special characters. Please remove any spaces or special characters from the name!'
            : undefined
        }
        inputProps={{
          maxLength: 40,
        }}
      />
    );
  const sql = (
    <>
      <div>SQL :</div>
      <CodeInput
        darkMode={theme.palette.mode !== 'light'}
        width={width - 100}
        value={record.sql ?? ''}
        onChange={(value) => {
          const sqlTest = value;
          update({ sql: sqlTest });
        }}
        language="sql"
      />
    </>
  );

  const additionalConnectionCode = (
    <ConnectionSelect
      data-testid="additionalconnection"
      label="Object Connection"
      appUid={appUid}
      hideCreateNew={true}
      value={record.objectConnectionCode ?? ''}
      onChange={(val) => {
        update({ objectConnectionCode: val });
        platform.recordLine(
          '',
          `await ui.type("additionalconnection", '${val}');`,
        );
      }}
    />
  ); // List of connections to show in drop down to select additional connection for ds

  async function downloadPackage() {
    if (!record) {
      return;
    }
    const attrs = await store.getAttributesForDataSource(
      appUid,
      record.datasourceUid ?? 'x',
      undefined,
    );
    const pkAttrs = await store.getPkAttributesForDataSource(
      appUid,
      record.datasourceUid ?? 'x',
      undefined,
    );
    record.callPost === 'Y'
      ? await downloadPLSQL(
          record.extTypeValue ?? record.typeValue ?? 'x',
          attrs,
          pkAttrs,
        )
      : await downloadPLSQLCRUD(
          record.extTypeValue ?? record.typeValue ?? 'x',
          attrs,
          pkAttrs,
        );
  }

  const onClose = () => {
    setUsageInfoPopup(false);
  };

  const onCloseEditDsPopup = () => {
    setDsNameEditPopup(false);
  };

  const openEditDsNamePopup = () => {
    setDsNameEditPopup(true);
  };

  return (
    <div className={classes.root}>
      {!fromDB && checkForLookups(record) ? (
        <Alert
          sx={{ marginTop: 2 }}
          severity="warning"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                platform.setter(activeSidebarPanelAtom, 'lookups');
              }}
            >
              Use Lookups Instead
            </Button>
          }
        >
          Are you trying to create a datasource to store lookup values?
        </Alert>
      ) : null}
      <Accordion
        className={accordianClasses.root}
        expanded={expanded}
        onChange={() => setExpanded((e) => !e)}
      >
        <AccordionSummary
          className={accordianClasses.header}
          expandIcon={<Icon iconName="chevron-down" />}
        >
          <Typography className={accordianClasses.heading}>Details</Typography>
        </AccordionSummary>
        <AccordionDetails className={accordianClasses.details}>
          <div className={accordianClasses.form}>
            <form className={classes.form} noValidate autoComplete="off">
              {fromTable ? tableName : name}
              {!fromTable && !fromSQL ? (
                <FormControl variant="standard" fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    inputProps={{ 'data-testid': 'ds-type' }}
                    disabled={fromDB || !!record.copy_from}
                    value={record.datasourceType ?? 'Table'}
                    onChange={(e) =>
                      update({
                        datasourceType: e.target.value as 'Table' | 'External',
                      })
                    }
                  >
                    <MenuItem value="Table">Table</MenuItem>
                    <MenuItem value="External">External</MenuItem>
                  </Select>
                </FormControl>
              ) : null}
              {record.datasourceType === 'External' ? (
                <>
                  <ExternalDataSourceSelect
                    disabled={fromDB}
                    label="External DataSource"
                    required
                    appUid={appUid}
                    value={record.typeValue ?? ''}
                    onChange={(val) => {
                      update({ typeValue: val });
                    }}
                  />
                  <TextField
                    data-testid="ext-obj-name"
                    autoFocus
                    disabled={fromDB}
                    label="External Object Name"
                    value={record.extTypeValue ?? ''}
                    onChange={(e) => {
                      const extTypeValue = e.target.value;
                      update({ extTypeValue });
                    }}
                    inputProps={{
                      maxLength: 128,
                    }}
                  />
                </>
              ) : fromTable ? (
                name
              ) : fromSQL ? (
                sql
              ) : (
                tableName
              )}
              {record.datasourceType !== 'External'
                ? additionalConnectionCode
                : null}
              {record.datasourceType === 'Table' && !fromSQL ? (
                <TextField
                  disabled={isViewOnlyUser}
                  data-testid="pkg-name"
                  label="Package Name"
                  value={record.extTypeValue ?? ''}
                  onChange={(e) => {
                    const extTypeValue = e.target.value;
                    update({ extTypeValue });
                  }}
                  inputProps={{
                    maxLength: 200,
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={downloadPackage}>
                          <Icon
                            iconName="download"
                            data-tip={'Download wrapper package'}
                          />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  placeholder="e.g. XXIO_TABLE_NAME_PKG"
                  helperText="If a package name is provided, it will be called instead of performing direct DML statements on the above table. You can click on the download icon to generate the package."
                />
              ) : null}

              {!fromTable ? (
                <FormControlLabel
                  disabled={isViewOnlyUser}
                  control={
                    <Checkbox
                      data-testid="skipQueryForUpdate"
                      checked={record.skipQueryForUpdate === 'Y'}
                      onChange={(e) =>
                        update({
                          skipQueryForUpdate: e.target.checked ? 'Y' : 'N',
                        })
                      }
                    />
                  }
                  label="Skip Query For Update"
                />
              ) : null}
              <FormControlLabel
                disabled={isViewOnlyUser}
                control={
                  <Checkbox
                    data-testid="callPostProcedure"
                    checked={record.callPost === 'Y'}
                    onChange={(e) =>
                      update({
                        callPost: e.target.checked ? 'Y' : 'N',
                      })
                    }
                  />
                }
                label="Call Post"
              />
              <FormControlLabel
                disabled={isViewOnlyUser}
                control={
                  <Checkbox
                    data-testid="readonly"
                    checked={record.readOnly === 'Y'}
                    onChange={(e) =>
                      update({
                        readOnly: e.target.checked ? 'Y' : 'N',
                      })
                    }
                  />
                }
                label="Readonly"
              />
              <SelectInput
                variant="standard"
                label="Time Zone"
                value={record.timeZone}
                helpText="If the datasource includes serverDate and serverDateTime attribute types, please select the appropriate timezone."
                error={true}
                onChange={(value) => {
                  update({ timeZone: value });
                }}
                options={timeZoneOptions}
              />
              {!(fromTable || fromScratch) ? (
                <FormControlLabel
                  disabled={isViewOnlyUser}
                  control={
                    <Checkbox
                      data-testid="preventResync"
                      checked={record.preventResync === 'Y'}
                      onChange={(e) =>
                        update({
                          preventResync: e.target.checked ? 'Y' : 'N',
                        })
                      }
                    />
                  }
                  label="Prevent ReSync"
                />
              ) : null}
            </form>
          </div>
        </AccordionDetails>
      </Accordion>

      {record.datasourceType &&
      fromDB &&
      ['Table', 'External'].includes(record.datasourceType) &&
      !fromTable &&
      !fromScratch ? (
        <AllScriptAccordians
          store={store}
          aggr={false}
          recordId={recordId ?? 'x'}
          setView={setView}
          appUid={appUid}
          isViewOnlyUser={isViewOnlyUser}
        />
      ) : null}
      {showUsageInfoPopup ? (
        <UsageInfoPopup
          appUid={appUid}
          datasourceUid={record.datasourceUid ?? ''}
          onClose={onClose}
          openEditDsNamePopup={openEditDsNamePopup}
          setView={setView}
        />
      ) : null}
      {showDsNameEditPopup ? (
        <EditDsName
          datasourceUid={record.datasourceUid ?? ''}
          onClose={onCloseEditDsPopup}
        />
      ) : null}
    </div>
  );
};

export default DataSourceEdit;
